import { AvailableMembershipPlan } from '@/models/api/paid-member-price/member-plan/available-membership-plan'
import { convertPlan } from '@/models/api/paid-member-price/member-plan/convert/scheme-to-model'
import { AvailablePaidMembershipPlanGroupScheme } from '@/models/api/paid-member-price/member-plan/group/scheme'

export class AvailablePaidMembershipPlanGroup {
  constructor(
    protected readonly group: AvailablePaidMembershipPlanGroupScheme
  ) {}

  get id(): string {
    return this.group.external_id
  }
  get displayLabel(): string {
    return this.group.display_label
  }

  get currentPlan(): AvailablePaidMembershipPlanInGroup | undefined {
    /*
     * 保留中の場合も、「現在のプラン」としてフォーカスして見せる必要がある
     * https://github.com/my-color/front/issues/6885
     */
    return this.plans.find(p => p.isActive() || p.holding())
  }

  get plansExcludeCurrentPlan(): AvailablePaidMembershipPlanInGroup[] {
    const currentPlan = this.currentPlan
    if (!currentPlan) {
      return this.plans
    }

    return this.plans.filter(p => p.externalId !== currentPlan.externalId)
  }

  get plans(): AvailablePaidMembershipPlanInGroup[] {
    return this.group.plans.map((p): AvailablePaidMembershipPlanInGroup => {
      const base = convertPlan(p)

      return Object.assign(base, {
        get belongingTo(): string {
          return p.belonging_to
        },
        get canSubscirbeAsNew(): boolean {
          return p.actions.subscribe_as_new.allowed
        },
        get canUnsubscribe(): boolean {
          return p.actions.unsubscribe.allowed
        },
        get upgrade(): PlanChangeAction<'upgrade'> {
          return {
            type: 'upgrade',
            planIdAfterChange: p.external_id,
            planIdBeforeChange: p.actions.upgrade.from,
            isAllowed: () => p.actions.upgrade.allowed,
          }
        },
        get downgrade(): PlanChangeAction<'downgrade'> {
          return {
            type: 'downgrade',
            planIdAfterChange: p.external_id,
            planIdBeforeChange: p.actions.downgrade.from,
            isAllowed: () => p.actions.downgrade.allowed,
          }
        },
        canUpgradeTo(after: AvailablePaidMembershipPlanInGroup): boolean {
          return this.isSameGroupWith(after)
            && p.subscribed
            && after.upgrade.isAllowed()
            && after.upgrade.planIdBeforeChange === p.external_id
        },
        canDowngradeTo(after: AvailablePaidMembershipPlanInGroup): boolean {
          return this.isSameGroupWith(after)
            && p.subscribed
            && after.downgrade.isAllowed()
            && after.downgrade.planIdBeforeChange === p.external_id
        },
        isSameGroupWith(other: AvailablePaidMembershipPlanInGroup): boolean {
          return this.belongingTo === other.belongingTo
        },
      })
    })
  }
}

export type AvailablePaidMembershipPlanInGroup = AvailableMembershipPlan & {
  readonly belongingTo: string
  readonly canSubscirbeAsNew: boolean
  /**
   * @deprecated 一時的な拡張であるため、原則再利用禁止
   * @see https://github.com/my-color/front/issues/6328
   */
  readonly canUnsubscribe: boolean

  readonly upgrade: PlanChangeAction<'upgrade'>
  readonly downgrade: PlanChangeAction<'downgrade'>

  canUpgradeTo(other: AvailablePaidMembershipPlanInGroup): boolean
  canDowngradeTo(other: AvailablePaidMembershipPlanInGroup): boolean
}

type AllowedActionType = 'upgrade' | 'downgrade'
export interface PlanChangeAction<T extends AllowedActionType> {
  readonly type: T
  readonly planIdAfterChange: string
  readonly planIdBeforeChange: string|null
  isAllowed(): this is AllowedAction<T>
}
export interface AllowedAction<T extends AllowedActionType> extends PlanChangeAction<T> {
  readonly planIdBeforeChange: string
}
